import * as Sentry from '@sentry/react';
import type { AxiosResponse } from 'axios';

import { logger } from '@/utils/logger/logger';

import { ApiError, ApiErrorCode, createApiError } from './';

const TAG = 'ApiErrorHandler:';

export interface ApiErrorResponse {
  status: number;
  message: string;
  code: number | string;
  /**
   * Backend args. Usually a string[] used for message interpolation, but some
   * structured errors (e.g. day-level time-off validation) send an object —
   * hence `unknown`. Array args reach `ApiError.backendArgs`; the full payload
   * is always preserved on `ApiError.backendArgsRaw`.
   */
  args?: unknown;
}

/**
 * Standard backend response envelope.
 * All API endpoints return `{ data, error? }`.
 * Use this as the generic type for `axiosInstance.get/post/etc.<ApiResponse<T>>`
 * so `withApiErrorHandler` can infer the inner data type correctly.
 */
export interface ApiResponse<T = unknown> {
  data: T;
  error?: ApiErrorResponse;
}

const isProdEnvironment = (): boolean => {
  return import.meta.env.VITE_ENV === 'prod' || import.meta.env.MODE === 'production';
};

const shouldLogToSentry = (status: number): boolean => {
  // Log 5xx errors (server errors) and 400 (bad request)
  return status === 400 || (status >= 500 && status < 600);
};

/**
 * Logs API error to Sentry with context
 */
const logErrorToSentry = (
  error: unknown,
  context: {
    endpoint: string;
    status?: number;
    code?: number | string;
    message?: string;
  }
): void => {
  if (!isProdEnvironment()) {
    logger.debug(TAG, 'Skipping Sentry logging in non-prod environment', context);
    return;
  }

  if (context.status && !shouldLogToSentry(context.status)) {
    logger.debug(TAG, 'Skipping Sentry logging for status', context.status);
    return;
  }

  Sentry.withScope(scope => {
    scope.setContext('api_error', {
      endpoint: context.endpoint,
      status: context.status,
      errorCode: context.code,
      message: context.message,
    });

    scope.setTag('error_type', 'api_error');
    scope.setTag('endpoint', context.endpoint);

    if (context.status) {
      scope.setTag('status_code', context.status);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(`API Error: ${context.endpoint}`, 'error');
    }

    logger.info(TAG, 'Error logged to Sentry', context);
  });
};

/**
 * Handles API response errors and throws appropriate ApiError
 */
export const handleApiResponse = <T>(
  response: AxiosResponse<ApiResponse<T>>,
  endpoint: string,
  errorCode: ApiErrorCode
): T => {
  if (response.data.error) {
    const errorData = response.data.error;
    const status = errorData.status;
    const code = errorData.code;
    const message = errorData.message;

    logger.error(TAG, 'API error response', {
      endpoint,
      status,
      code,
      message,
    });

    logErrorToSentry(new Error(message || 'Unknown error'), {
      endpoint,
      status,
      code,
      message,
    });

    const numericCode = typeof code === 'number' ? code : undefined;
    const rawArgs = errorData.args;
    // Only array args feed `backendArgs` (used for i18n interpolation); the raw
    // payload (incl. structured objects) is always preserved on `backendArgsRaw`.
    const args = Array.isArray(rawArgs) ? (rawArgs as string[]) : undefined;
    throw createApiError(
      errorCode,
      response.data.error,
      message,
      numericCode,
      args,
      status,
      rawArgs
    );
  }

  return response.data.data;
};

/**
 * Handles caught errors from API calls
 */
export const handleApiError = (
  error: unknown,
  endpoint: string,
  errorCode: ApiErrorCode
): never => {
  if (error instanceof ApiError) {
    throw error;
  }

  let status: number | undefined;
  let message: string | undefined;
  let code: string | undefined;
  let args: string[] | undefined;
  let rawArgs: unknown;

  // Extract message from Error instance
  if (error instanceof Error) {
    message = error.message;
  }

  // Extract additional details from axios error response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { status?: number; data?: any };
      message?: string;
    };

    status = axiosError.response?.status;

    // Extract from response.data.error structure
    if (axiosError.response?.data?.error) {
      const errorData = axiosError.response.data.error;

      if (errorData.message) {
        message = errorData.message;
      }
      if (errorData.status) {
        status = errorData.status;
      }
      if (errorData.code) {
        code = errorData.code;
      }
      // Preserve the raw args (structured objects included); array args also
      // feed `backendArgs` for i18n interpolation.
      rawArgs = errorData.args;
      if (Array.isArray(errorData.args)) {
        args = errorData.args;
      }
    }

    // Fallback to axios error message
    if (!message && axiosError.message) {
      message = axiosError.message;
    }
  }

  logger.error(TAG, 'API call failed', {
    endpoint,
    status,
    code,
    message,
    error,
  });

  logErrorToSentry(error, {
    endpoint,
    status,
    code,
    message,
  });

  const numericCode = code !== undefined ? Number(code) : undefined;
  const validBackendCode = numericCode && !isNaN(numericCode) ? numericCode : undefined;
  throw createApiError(errorCode, error, message, validBackendCode, args, status, rawArgs);
};

interface WithApiErrorHandlerOptions<T> {
  /** Optional mock function that returns data directly, bypassing the API call */
  mock?: () => Promise<T> | T;
}

/**
 * Wrapper for API calls that handles errors consistently.
 * Encapsulates the entire try/catch/error handling logic.
 *
 * @param endpoint - API endpoint URL for logging
 * @param errorCode - Error code to use if request fails
 * @param apiCall - Function that performs the actual API request
 * @param options - Optional configuration including mock function
 * @returns The unwrapped data from the API response
 *
 * @example
 * ```typescript
 * // Real API call
 * export const getProjects = (params: GetProjectsParams) =>
 *   withApiErrorHandler(
 *     '/api/projects/summaries/search',
 *     ApiErrorCode.PROJECTS_LOAD_FAILED,
 *     () => axiosInstance.post(endpoint, { filter: params.filters })
 *   );
 *
 * // With mock data (for development when backend is not ready)
 * export const getProjects = (params: GetProjectsParams) =>
 *   withApiErrorHandler(
 *     '/api/projects/summaries/search',
 *     ApiErrorCode.PROJECTS_LOAD_FAILED,
 *     () => axiosInstance.post(endpoint, { filter: params.filters }),
 *     {
 *       mock: () => new Promise(resolve =>
 *         setTimeout(() => resolve(mockProjectsData), 500)
 *       )
 *     }
 *   );
 * ```
 */
export const withApiErrorHandler = async <T>(
  endpoint: string,
  errorCode: ApiErrorCode,
  apiCall: () => Promise<AxiosResponse<ApiResponse<T>>>,
  options?: WithApiErrorHandlerOptions<T>
): Promise<T> => {
  // Use mock data if provided
  if (options?.mock) {
    logger.debug(TAG, 'Using mock data for endpoint', { endpoint });
    return Promise.resolve(options.mock());
  }

  try {
    const response = await apiCall();
    return handleApiResponse(response, endpoint, errorCode);
  } catch (error) {
    return handleApiError(error, endpoint, errorCode);
  }
};
